<template>
  <div class="line-chart" :style="{width: config.chartWidth+'px',height:config.chartHeight+'px'}">
    <div class="line-chart-wrapper">
      <img v-if="borderImgUrl" class="line-chart-border" :src="borderImgUrl" alt=""
           :style="chartBorderTemplateImageStyle">
      <div class="line-chart-title" :style="titleBarInlineStyle">
        {{ (activatedLabel ? activatedLabel+ ' ': '') + config.chartTitle }}
        <el-tooltip v-if="config.chartViewData.showIndicator" class="item" :open-delay="500" effect="dark" placement="top">
          <div slot="content" v-html="indicatorDescription"></div>
          <i class="el-icon-question"></i>
        </el-tooltip>
      </div>
    </div>
    <div ref="line" class="line-chart-container"></div>
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
// import { getNumberFormat } from '@/utils/common'
import { COLOR_LIST } from '@/utils/defaultColorData'
import { imgURLtoBase64, hexToRgba } from '@/utils/common'
import { relateBus } from '@/eventBus'
import { lineBaseOption } from './options'
import ChartMixin from './mixin'
import { getXAxisLabelRotateDegrees, gridBottomForXAxisLabelRotate } from '@/utils/chartAxisLabel'

export default {
  mixins: [ChartMixin],

  data () {
    return {
      activatedLabel: (this.config.chartSet.chartStyle.isLinkMap && this.config.chartViewData.chartData[0]?.labelName) || '',
    }
  },
  computed: {
    isMultipleChartData () {
      const data = this.config.chartViewData.chartData
      return !!data?.[0]?.labelName
    },
    dataIndex () {
      if (this.isMultipleChartData && this.style.chartStyle.isLinkMap) {
        const i = this.config.chartViewData.chartData.findIndex(bar => bar.labelName === this.activatedLabel)
        return i === -1 ? 0 : i
      } else {
        return 0
      }
    },
    // 表格数据
    chartData () {
      if (this.isMultipleChartData && this.style.chartStyle.isLinkMap) {
        return this.config.chartViewData.chartData[this.dataIndex].realChartData
      } else {
        return this.config.chartViewData.chartData
      }
    },
    // 双轴配置：从 chartSet.chartStyle.axisConfig 读取，格式为 { col_1: 0, col_2: 1, y_0: 0, y_1: 1, ... }
    setAxis () {
      return this.style.chartStyle.axisConfig || null
    },
    style () {
      return this.config.chartSet
    },
    // 维度
    dataAxis () {
      const list = []
      if (this.chartData?.length && Array.isArray(this.chartData)) {
        const colKeyList = Object.keys(this.chartData[0])
        this.chartData.slice(1).forEach(bar => {
          list.push(bar[colKeyList[0]]) // 第一列数据(维度)
        })
        return list
      } else {
        return []
      }
    },
    // 数据
    series () {
      const list = []
      const initItem = {
        name: '',
        type: 'line',
        smooth: this.style.chartStyle.smooth,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 2,
        },
        data: [],
      }
      // 面积图 areaStyle 按系列索引在 push 时合并（渐变需按系列色）
      //  双轴设置，yAxisIndex 设置数据对应的坐标轴，默认为0
      const doubleAxisItem = {
        ...initItem,
        yAxisIndex: 1,
        itemStyle: {
          normal: {
            lineStyle: {
              type: 'dotted',
            },
          },
        },
      }
      if (!this.chartData?.length) {
        return
      }
      // 除第一列(维度)之外的所有表头key集合 keyList
      const colKeyList = Object.keys(this.chartData[0]).slice(1)
      // 表头
      const tableHeader = this.chartData[0]
      // 表格数据 (去除第一行(表头))
      const tableData = this.chartData.slice(1)
      // 遍历除第一列(维度)之外的所有列
      colKeyList.forEach((key, index) => {
        // 排除空数据
        if (!tableData.every(row => (row[key] === '' || row[key] === undefined))) {
          // doubleAxis 开关开启且 setAxis 有效时，才读取轴配置；否则默认 y1（0）
          const axisIndex = (this.style.chartStyle?.doubleAxis && this.setAxis && this.setAxis[key] != null)
            ? this.setAxis[key]
            : 0
          list.push({
            ...(axisIndex ? doubleAxisItem : initItem),
            ...(this.config.chartComponent === 'areaLine' ? this.areaStyleForSeriesIndex(list.length) : {}),
            name: tableHeader[key], // 当前数据列的表头
            data: tableData.map(bar => bar[key]), // 当前数据列
          })
        }
      })

      return list
    },
    /** X 轴旋转 + 底部留白：聚合监听，避免 Vue2 对深层字段监听失效 */
    xAxisLabelLayoutSignature () {
      const cs = this.style?.chartStyle
      if (!cs) return '0|5'
      const rot = Number(cs.xAxisLabelRotate)
      const bp = Number(cs.xAxisLabelRotateBottomPercent)
      return `${Number.isFinite(rot) ? rot : 0}|${Number.isFinite(bp) ? bp : 5}`
    },

  },

  watch: {
    isMultipleChartData (val) {
      if (!val) {
        this.activatedLabel = ''
      }
    },

    // 全局样式监听
    resolvedChartColorsLabel () {
      if (!this.charts) return
      // 面积渐变把颜色写进 areaStyle，需整表重算
      if (this.config.chartComponent === 'areaLine' && this.style.chartStyle.areaGradientFade) {
        this.init()
        return
      }
      this.option.color = COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0].value
      this.charts.setOption(this.option, true)
    },
    // 配置项变更
    config: {
      handler: function () {
        this.init()
      },
      deep: true,
    },
    xAxisLabelLayoutSignature () {
      if (!this.charts) return
      this.$nextTick(() => this.init())
    },
    async borderStyle (val) {
      if (val) {
        this.borderImgUrl = await imgURLtoBase64(val)
      } else {
        this.borderImgUrl = ''
      }
    },
  },

  async created () {
    if (this.borderStyle) {
      this.borderImgUrl = await imgURLtoBase64(this.borderStyle)
    }
    if (!this.style.chartStyle?.carouselDelay) { this.$set(this.config.chartSet.chartStyle, 'carouselDelay', 3) }
    // if (!this.style.chartStyle?.isLegend) { this.$set(this.config.chartSet.chartStyle, 'isLegend', false) }
    if (!this.style.chartStyle?.legendSize) { this.$set(this.config.chartSet.chartStyle, 'legendSize', 12) }
    if (this.config.chartComponent === 'areaLine' && this.style.chartStyle.areaGradientFade === undefined) {
      this.$set(this.config.chartSet.chartStyle, 'areaGradientFade', false)
    }
    if (this.style.chartStyle.xAxisLabelRotate === undefined) {
      this.$set(this.config.chartSet.chartStyle, 'xAxisLabelRotate', 0)
    }
    if (this.style.chartStyle.xAxisLabelRotateBottomPercent === undefined) {
      this.$set(this.config.chartSet.chartStyle, 'xAxisLabelRotateBottomPercent', 5)
    }
  },
  mounted () {
    if (!this.charts) {
      this.charts = echarts.init(this.$refs.line)
    }
    this.init()
    relateBus.$on('activeChange', (mapAddSn, data) => {
      if (this.style.chartStyle.isLinkMap && data.name && this.config.chartSet.chartStyle.linkMap === mapAddSn) {
        this.activatedLabel = data.name
        this.init()
      }
    })
  },
  methods: {
    /** 面积图填充：与当前配色一致；开启渐变时自上而下过渡到黑色半透明 */
    areaStyleForSeriesIndex (seriesIndex) {
      const palette = COLOR_LIST.filter(item => item.label === this.resolvedChartColorsLabel)[0].value
      const hex = palette[seriesIndex % palette.length]
      if (this.style.chartStyle.areaGradientFade) {
        return {
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: hexToRgba(hex, 0.5) },
              { offset: 1, color: 'rgba(0,0,0,0.45)' },
            ]),
          },
        }
      }
      return {
        areaStyle: {
          opacity: 0.5,
        },
      }
    },
    init () {
      this.option = _.merge(_.cloneDeep(lineBaseOption), {
        grid: {
          top: this.style.chartStyle.isLegend ? '46px' : '30px',
          bottom: gridBottomForXAxisLabelRotate(this.style.chartStyle, '10%'),
        },
        tooltip: {
          textStyle: {
            fontSize: this.style.chartStyle.fontSize,
          },
        },
        color: COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0].value,
        xAxis: {
          // 填充数据
          data: this.dataAxis,
          axisLabel: {
            fontSize: this.style.chartStyle.fontSize,
            rotate: getXAxisLabelRotateDegrees(this.style.chartStyle),
          },
        },
        legend: {
          show: this.style.chartStyle.isLegend,
          textStyle: {
            fontSize: this.style.chartStyle.legendSize,
          },
        },
        yAxis: this.style.chartStyle.doubleAxis ? [
          {
            axisLabel: {
              fontSize: this.style.chartStyle.fontSize,
            },
          },
          {
            axisLabel: {
              fontSize: this.style.chartStyle.fontSize,
            },
          },
        ] : [
          {
            axisLabel: {
              fontSize: this.style.chartStyle.fontSize,
            },
          },
        ],
        label: {
          show: this.style.chartStyle.isLabel,
          fontSize: this.style.chartStyle.fontSize,
        },
        series: this.series,
      })

      // 再次写入 grid，确保底部留白随 chartStyle 立刻生效（lodash 合并偶发未触发 ECharts 差异更新）
      const gt = this.style.chartStyle.isLegend ? '46px' : '30px'
      const gb = gridBottomForXAxisLabelRotate(this.style.chartStyle, '10%')
      this.option.grid = { ...this.option.grid, top: gt, bottom: gb }

      this.charts.setOption(this.option, true)

      // 是否轮播 tooltips
      if (this.style.chartStyle.carousel && this.series?.length > 0) {
        this.tooltipsActive()
        this.mouseEvents()
      } else {
        this.charts.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        if (this.timer) {
          clearInterval(this.timer)
          this.index = -1
        }
      }
      // resize
      this.$nextTick(() => {
        this.charts.resize()
      })
    },

  },
}
</script>

<style lang='scss' scoped>
.line-chart{
  display: flex;
  flex-direction: column;
  &-wrapper {
    position: relative;
    width:100%;
    overflow: visible;
  }
  &-border {
    position: absolute;
    top: 0;
    height: 100%;
    z-index: -1;

  }
  &-title {
    padding:7px 30px;
  }
  .line-chart-container{
    flex: 1;
  }
}
</style>
